import React, { useState, useContext } from "react";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaChevronDown, FaChevronUp, FaSearch, FaPlus, FaEye, FaFileExport } from "react-icons/fa";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import { useNavigate } from "react-router-dom";

const AdvanceListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate=useNavigate();

  const [formData, setFormData] = useState({
    empCode: "",
    empName: "",
    date: "",
    site: "HEAD-OFFICE",
    department: "",
  });

  const [showFilters, setShowFilters] = useState(true);
  const [records, setRecords] = useState([]);

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSearch = () => {
    // For now just show "No Records"
    setRecords([]);
    toast.info("Searching advance records...");
  };

  const handleViewAll = () => {
    // Example static records
    const sampleRecords = [
      {
        id: 1,
        empCode: "101",
        empName: "Ramesh",
        site: "HEAD-OFFICE",
        department: "Finance",
        date: "2025-09-01",
        amount: "₹5,000",
        status: "Pending"
      },
      {
        id: 2,
        empCode: "102",
        empName: "Suresh",
        site: "HEAD-OFFICE",
        department: "HR",
        date: "2025-09-03",
        amount: "₹8,000",
        status: "Approved"
      },
      {
        id: 3,
        empCode: "103",
        empName: "Priya",
        site: "HILARA",
        department: "IT",
        date: "2025-09-05",
        amount: "₹3,000",
        status: "Rejected"
      },
      {
        id: 4,
        empCode: "104",
        empName: "Ankit",
        site: "HEAD-OFFICE",
        department: "Finance",
        date: "2025-09-07",
        amount: "₹7,500",
        status: "Approved"
      },
      {
        id: 5,
        empCode: "105",
        empName: "Meera",
        site: "HILARA",
        department: "HR",
        date: "2025-09-10",
        amount: "₹6,200",
        status: "Pending"
      }
    ];
    setRecords(sampleRecords);
    toast.success("Showing all advance records");
  };

  const handleAddNew = () => {
    navigate("/PayRoll/AdvanceEntry");
    toast.info("Opening new advance form...");
  };

  const handleExport = () => {
    if (records.length === 0) {
      toast.error("No records to export!");
      return;
    }
    toast.success("Exporting advance records...");
  };

  const clearAllFilters = () => {
    setFormData({
      empCode: "",
      empName: "",
      date: "",
      site: "HEAD-OFFICE",
      department: "",
    });
    toast.success("All filters cleared");
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "Approved":
        return isDark ? "bg-green-800 text-green-100" : "bg-green-100 text-green-800";
      case "Pending":
        return isDark ? "bg-yellow-800 text-yellow-100" : "bg-yellow-100 text-yellow-800";
      case "Rejected":
        return isDark ? "bg-red-800 text-red-100" : "bg-red-100 text-red-800";
      default:
        return isDark ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-700";
    }
  };

  return (
    <PayRollLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ADVANCE LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white text-black`
        }`}>
          {/* Header Section */}
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${
                isDark ? "text-white" : "text-gray-900"
              }`}>
                Advance Listing
              </h1>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark 
                  ? "bg-blue-900 text-blue-100" 
                  : "bg-blue-100 text-blue-800"
              }`}>
                {records.length} Records
              </span>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Hide/Show Filters Button */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark 
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" 
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              {/* Reset Form Button */}
              <button
                onClick={clearAllFilters}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark 
                    ? "bg-orange-700 hover:bg-orange-600 text-white" 
                    : "bg-orange-600 hover:bg-orange-700 text-white"
                }`}
              >
                Reset Form
              </button>

              <button
                onClick={handleExport}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark 
                    ? "bg-purple-700 hover:bg-purple-600 text-white" 
                    : "bg-purple-600 hover:bg-purple-700 text-white"
                }`}
              >
                <FaFileExport />
                Export
              </button>

              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark 
                    ? "bg-green-700 hover:bg-green-600 text-white" 
                    : "bg-green-600 hover:bg-green-700 text-white"
                }`}
              >
                <FaPlus />
                Add New
              </button>
            </div>
          </div>

          {/* Collapsible Filters Section */}
          <div className={`rounded-xl border mb-6 overflow-hidden transition-all duration-300 ${
            isDark 
              ? "border-gray-700" 
              : "border-gray-200"
          }`}>
            {/* Filter Header - Always Visible */}
            <div 
              className={`p-4 cursor-pointer transition-colors duration-200 ${
                isDark 
                  ? "bg-gray-800 hover:bg-gray-750" 
                  : "bg-gray-50 hover:bg-gray-100"
              }`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <FaSearch /> ADVANCE FILTRATION
                </h3>
                <div className="flex items-center gap-3">
                  <span className={`text-sm ${
                    isDark ? "text-gray-400" : "text-gray-600"
                  }`}>
                    Click to {showFilters ? 'hide' : 'show'} filters
                  </span>
                  {showFilters ? (
                    <FaChevronUp className="text-lg" />
                  ) : (
                    <FaChevronDown className="text-lg" />
                  )}
                </div>
              </div>
            </div>

            {/* Filter Content - Collapsible */}
            <div className={`transition-all duration-300 ease-in-out ${
              showFilters 
                ? 'max-h-96 opacity-100' 
                : 'max-h-0 opacity-0 overflow-hidden'
            }`}>
              <div className={`p-6 border-t ${
                isDark 
                  ? "bg-gray-800 border-gray-700" 
                  : "bg-gray-50 border-gray-200"
              }`}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Employee Code */}
                  <div>
                    <label className={labelClass}>Employee Code</label>
                    <input
                      type="text"
                      name="empCode"
                      value={formData.empCode}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="Enter employee code"
                    />
                  </div>
                  
                  {/* Employee Name */}
                  <div>
                    <label className={labelClass}>Employee Name</label>
                    <input
                      type="text"
                      name="empName"
                      value={formData.empName}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="Enter employee name"
                    />
                  </div>
                  
                  {/* Site Name */}
                  <div>
                    <label className={labelClass}>Site Name</label>
                    <select
                      name="site"
                      value={formData.site}
                      onChange={handleChange}
                      className={inputClass}
                    >
                      <option value="HEAD-OFFICE">HEAD-OFFICE</option>
                      <option value="HILARA">HILARA</option>
                    </select>
                  </div>
                  
                  {/* Department */}
                  <div>
                    <label className={labelClass}>Department</label>
                    <select
                      name="department"
                      value={formData.department}
                      onChange={handleChange}
                      className={inputClass}
                    >
                      <option value="">Select</option>
                      <option value="Finance">Finance</option>
                      <option value="HR">HR</option>
                      <option value="IT">IT</option>
                    </select>
                  </div>
                  
                  {/* Date */}
                  <div>
                    <label className={labelClass}>Date</label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      className={inputClass}
                    />
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={clearAllFilters}
                    className={`text-sm font-medium transition-colors duration-200 ${
                      isDark 
                        ? "text-gray-400 hover:text-white" 
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    Clear All Fields
                  </button>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={handleViewAll}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                        isDark 
                          ? "bg-gray-700 hover:bg-gray-600 text-white" 
                          : "bg-gray-300 hover:bg-gray-400 text-gray-700"
                      }`}
                    >
                      <FaEye />
                      View All
                    </button>
                    
                    <button
                      onClick={handleSearch}
                      className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
                    >
                      <FaSearch />
                      Search Records
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Records Table */}
          <div className={`rounded-xl shadow-sm border overflow-hidden ${
            isDark ? "border-gray-700" : "border-gray-200"
          }`}>
            <div className="overflow-x-auto">
              {records.length === 0 ? (
                <div className={`p-8 text-center ${
                  isDark ? "bg-gray-900 text-gray-400" : "bg-white text-gray-500"
                }`}>
                  <div className="text-lg font-medium mb-2">No Records Found!</div>
                  <p className="text-sm">Use the search filters above to find advance records</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold">S.No</th>
                      <th className="px-4 py-4 text-center font-semibold">Employee Code</th>
                      <th className="px-4 py-4 text-center font-semibold">Employee Name</th>
                      <th className="px-4 py-4 text-center font-semibold">Site</th>
                      <th className="px-4 py-4 text-center font-semibold">Department</th>
                      <th className="px-4 py-4 text-center font-semibold">Date</th>
                      <th className="px-4 py-4 text-center font-semibold">Amount</th>
                      <th className="px-4 py-4 text-center font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {records.map((r, i) => (
                      <tr key={r.id} className={`transition-colors duration-200 ${
                        isDark ? "bg-gray-900" : "bg-white"
                      }`}>
                        <td className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}>
                          {i + 1}
                        </td>
                        <td className={`px-4 py-4 whitespace-nowrap text-sm text-center font-bold ${
                          isDark ? "text-blue-300" : "text-blue-600"
                        }`}>
                          {r.empCode}
                        </td>
                        <td className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}>
                          {r.empName}
                        </td>
                        <td className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}>
                          {r.site}
                        </td>
                        <td className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}>
                          {r.department}
                        </td>
                        <td className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}>
                          {r.date}
                        </td>
                        <td className={`px-4 py-4 whitespace-nowrap text-sm text-center font-bold ${
                          isDark ? "text-green-300" : "text-green-600"
                        }`}>
                          {r.amount}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(r.status)}`}>
                            {r.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default AdvanceListing;